import { Carousel } from '@mantine/carousel'
import {
  Box,
  Card,
  Flex,
  Grid,
  MultiSelect,
  Pagination,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import TitleDivider from '../../components/divider'
import ImageFallback from '../../components/image_fallback'
import Layout from '../../components/layout'
import styles from '../../styles/Home.module.css'
import { searchAPI_v2 } from '../../utils/axios'
import prerenderImportantLocales from '../../utils/prerender_locales.js'

const Platforms = ({ topPlatforms, availablePlatforms, country }) => {
  const [regions, setRegions] = useState([])
  const [businessModels, setBusinessModels] = useState([])
  const [countries, setCountries] = useState([])
  const [searchText, setSearchText] = useState('')

  // Pagination
  const itemsPerPage = 63 // Number of items per page
  const [currentPage, setCurrentPage] = useState(1)

  const { locale } = useRouter()

  const t = useTranslations('Services')
  const t_country = useTranslations('PrimaryCountryCode')

  const uniquePlatformsByName = []
  const seen = new Set()

  for (const item of availablePlatforms) {
    const identifier = `${item.id}-${item.name}`
    if (!seen.has(identifier)) {
      seen.add(identifier)
      uniquePlatformsByName.push(item)
    }
  }

  const filteredItems = [...uniquePlatformsByName]
    .filter((item) => {
      const matchesRegion =
        regions.length === 0 ||
        regions.some((region) => item.regions?.includes(region))
      const matchesBusinessModel =
        businessModels.length === 0 ||
        businessModels.some((model) => item.revenue_model?.includes(model))
      const matchesCountry =
        countries.length === 0 ||
        countries.some((country) => item.origin_countries?.includes(country)) // Cambiar aquí
      const matchesSearch =
        !searchText || item.name.toLowerCase().includes(searchText.toLowerCase())
      return matchesRegion && matchesBusinessModel && matchesCountry && matchesSearch
    })
    .sort((a, b) => a.name.localeCompare(b.name))

  // Paginate filtered items
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Extraer datos únicos para los selects
  const regionOptions = Array.from(
    new Set(uniquePlatformsByName.flatMap((item) => item.regions).filter(Boolean))
  ).sort()

  const businessModelOptions = Array.from(
    new Set(
      uniquePlatformsByName.flatMap((item) => item.revenue_model).filter(Boolean)
    )
  ).sort()

  // const countryOptions = Array.from(
  //   new Set(
  //     uniquePlatformsByName.flatMap((item) => item.country_iso).filter(Boolean)
  //   )
  // )
  //   .sort()
  //   .map((country) => ({ value: country, label: t_country(country) }))

  const countryOptions = Array.from(
    new Set(
      uniquePlatformsByName.flatMap((item) => item.origin_countries).filter(Boolean)
    )
  ).sort()

  const renderColumns = (items) => {
    const sortedItems = [...items].sort((a, b) => {
      if (a.logo && !b.logo) return -1
      if (!a.logo && b.logo) return 1
      return 0
    })

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '28px',
          justifyContent: 'center',
          width: '100%',
          margin: '0 auto'
        }}
      >
        {sortedItems.map((item) => {
          const platformSlug = item.name.toLowerCase().replace(/\s/g, '')
          return (
            <Link href={`/services/${item.id}-${platformSlug}`} key={item.id}>
              <Card
                p={0}
                radius={4}
                sx={{
                  backgroundColor: '#0E1922',
                  borderBottom: '2px solid #7B39B6',
                  margin: '0 auto'
                }}
                h={216}
                w={144}
              >
                <Box sx={{ position: 'relative', width: '100%', height: '76%' }}>
                  {item.logo ? (
                    <ImageFallback
                      src={`https://wsrv.nl/?url=${encodeURIComponent(
                        item.logo
                      )}&w=360&h=410&fit=cover&output=webp&ll`}
                      fallbackSrc="/noimage.png"
                      width="100%"
                      height="100%"
                      style={{ objectFit: 'cover' }}
                      alt={`${item.name}`}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0E1922',
                        color: '#fff'
                      }}
                    >
                      <Image
                        alt="platform"
                        src="/noimage.png"
                        width={30}
                        height={30}
                      />
                    </Box>
                  )}
                </Box>
                <Stack p={25} spacing={0} align="center">
                  <Text
                    size={12}
                    tt="uppercase"
                    color="white"
                    sx={{
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%'
                    }}
                  >
                    {item.name}
                  </Text>
                </Stack>
              </Card>
            </Link>
          )
        })}
      </div>
    )
  }

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': t('pageTitle'),
    'description': t('pageDescription'),
    'url': `https://www.streamdiscover.com/${locale}/platforms`, // URL actualizada con `locale`
    'inLanguage': locale // Idioma especificado con `locale`
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'itemListElement': topPlatforms.map((platform, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'BroadcastService',
        'name': platform.platform,
        'logo': platform.logo || '/noimage.png',
        'url': `https://www.streamdiscover.com/${locale}/services/${
          platform.id
        }-${platform.platform.toLowerCase().replace(/\s/g, '')}`
      }
    }))
  }

  return (
    <>
      <Head>
        <title>{t('pageTitle')}</title>
        <meta name="description" content={t('pageDescription')} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />

        <meta property="og:title" content={t('pageTitle')} />
        <meta property="og:description" content={t('pageDescription')} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://www.streamdiscover.com/path/to/preview-image.jpg"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pageTitle')} />
        <meta name="twitter:description" content={t('pageDescription')} />
        <meta
          name="twitter:image"
          content="https://www.streamdiscover.com/path/to/preview-image.jpg"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <main className={styles.main}>
        <Stack
          m="auto"
          sx={(theme) => ({
            width: '80vw',
            maxWidth: '1366px',
            [theme.fn.smallerThan('md')]: {
              width: '85vw'
            },
            [theme.fn.smallerThan('sm')]: {
              width: '90vw'
            }
          })}
        >
          <Stack sx={{ gap: '0' }}>
            <TitleDivider
              titleText={`${t('popularStreamingServices')} ${t_country(
                locale.split('-')[1]
              )}`.toUpperCase()}
            />
            <Carousel
              slideSize="10%"
              slideGap="xs"
              align="start"
              loop={false}
              height={100}
              sx={(theme) => ({
                maxWidth: '100%',
                [theme.fn.smallerThan('sm')]: {
                  slideSize: '30%'
                }
              })}
              styles={{
                control: {
                  display: 'none',
                  opacity: 10,
                  cursor: 'default',
                  backgroundColor: 'transparent',
                  border: 'transparent',
                  transform: 'none',
                  transition: 'none',
                  '&[data-inactive]': {
                    opacity: 0,
                    cursor: 'default'
                  }
                }
              }}
            >
              {topPlatforms.map((platform) => {
                const platformSlug = platform.platform
                  .toLowerCase()
                  .replace(/\s/g, '')
                return (
                  <Carousel.Slide key={platform.id}>
                    <Link
                      href={`/services/${platform.id}-${platformSlug}`}
                      passHref
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Flex
                        align="center"
                        direction="column"
                        m={10}
                        style={{ justifyContent: 'center' }}
                      >
                        <Image
                          src={platform.logo || '/noimage.png'}
                          width={75}
                          height={75}
                          style={{ borderRadius: '4px', zIndex: 99 }}
                          alt="platform logo"
                          onError={(e) => {
                            e.target.src = '/noimage.png'
                          }}
                        />
                      </Flex>
                    </Link>
                  </Carousel.Slide>
                )
              })}
            </Carousel>
          </Stack>

          <Stack sx={{ gap: '0' }}>
            <TitleDivider titleText={t('allStreamingServices')} />
          </Stack>
          <Flex
            gap="md"
            mb="lg"
            direction={{ base: 'column', md: 'row' }} // Columna en pantallas pequeñas, fila en medianas y grandes
            style={{ justifyContent: 'space-between', width: '100%' }}
          >
            <MultiSelect
              data={regionOptions}
              placeholder={t('selectRegions')}
              value={regions}
              onChange={setRegions}
              searchable
              dropdownPosition="bottom"
              nothingFound="No options"
              styles={(theme) => ({
                input: {
                  fontSize: theme.fontSizes.sm,
                  backgroundColor: '#121E274D',
                  borderRadius: '10px',
                  border: 'none',
                  borderBottom: '2px solid #7B39B6'
                }
              })}
              style={{ flex: 1 }}
            />
            <MultiSelect
              data={businessModelOptions}
              placeholder={t('selectBusinessModels')}
              value={businessModels}
              onChange={setBusinessModels}
              searchable
              dropdownPosition="bottom"
              nothingFound="No options"
              styles={(theme) => ({
                input: {
                  fontSize: theme.fontSizes.sm,
                  backgroundColor: '#121E274D',
                  borderRadius: '10px',
                  border: 'none',
                  borderBottom: '2px solid #7B39B6'
                }
              })}
              style={{ flex: 1 }}
            />
            <MultiSelect
              data={countryOptions}
              placeholder={t('selectCountryOrigin')}
              value={countries}
              onChange={setCountries}
              searchable
              dropdownPosition="bottom"
              nothingFound="No options"
              styles={(theme) => ({
                input: {
                  fontSize: theme.fontSizes.sm,
                  backgroundColor: '#121E274D',
                  borderRadius: '10px',
                  border: 'none',
                  borderBottom: '2px solid #7B39B6'
                }
              })}
              sx={(theme) => ({
                flex: 1,
                [theme.fn.smallerThan('md')]: { flex: '1 1 100%' } // Ocupa el 100% en pantallas pequeñas
              })}
            />
            <TextInput
              placeholder={t('search')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              styles={(theme) => ({
                input: {
                  fontSize: theme.fontSizes.sm,
                  backgroundColor: '#121E274D',
                  borderRadius: '10px',
                  border: 'none',
                  borderBottom: '2px solid #7B39B6'
                }
              })}
              style={{ flex: 1 }}
            />
          </Flex>

          <Text>
            Showing{' '}
            {Math.min((currentPage - 1) * itemsPerPage + 1, filteredItems.length)}-
            {Math.min(currentPage * itemsPerPage, filteredItems.length)} of{' '}
            {filteredItems.length} results
          </Text>
          <Grid style={{ color: '#D9D9D9' }} gutter="md">
            {renderColumns(paginatedItems)}
          </Grid>
          <Pagination
            total={Math.ceil(filteredItems.length / itemsPerPage)}
            value={currentPage}
            onChange={setCurrentPage}
            styles={(theme) => ({
              control: {
                fontSize: theme.fontSizes.sm,
                backgroundColor: '#121E274D',
                borderRadius: '10px',
                border: 'none',
                borderBottom: '2px solid #7B39B6',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1A2330'
                }
              },
              active: {
                backgroundColor: '#7B39B6',
                color: 'white',
                fontWeight: 'bold'
              }
            })}
            style={{ marginTop: '20px', justifyContent: 'center', display: 'flex' }}
          />
        </Stack>
      </main>
    </>
  )
}

Platforms.getLayout = function getLayout(page) {
  return (
    <Layout withAutocomplete showHero={true} pageName="Services">
      {page}
    </Layout>
  )
}

export default Platforms

export async function getStaticProps({ locale }) {
  prerenderImportantLocales(locale)

  const country = locale.split('-')[1].toLowerCase()
  console.log({ country })

  const { data: topPlatformsResponse } = await searchAPI_v2(
    `/platforms/top?country=${country}`
  )

  const topPlatforms = Array.isArray(topPlatformsResponse.data)
    ? topPlatformsResponse.data
    : []

  const { data: availablePlatformsData } = await searchAPI_v2(
    '/platforms/?limit=4998&active_cp=true'
  )
  console.log('dataLenth', availablePlatformsData.data.length)

  const groupedPlatforms = availablePlatformsData.data.reduce((acc, platform) => {
    const existingPlatform = acc.find((p) => p.id === platform.id)

    if (existingPlatform) {
      existingPlatform.country_iso = [
        ...new Set([...(existingPlatform.country_iso || []), platform.country_iso])
      ]
      existingPlatform.regions = [
        ...new Set([
          ...(existingPlatform.regions || []),
          ...(platform.regions || [])
        ])
      ]
      existingPlatform.revenue_model = [
        ...new Set([
          ...(existingPlatform.revenue_model || []),
          ...(platform.revenue_model || [])
        ])
      ]
      existingPlatform.origin_countries = [
        ...new Set([
          ...(existingPlatform.origin_countries || []),
          ...(platform.origin_countries || [])
        ])
      ]
    } else {
      acc.push({
        id: platform.id,
        name: platform.name,
        platform_code: platform.platform_code,
        logo: platform.logo_url || null,
        revenue_model: [...(platform.revenue_model || [])],
        country_iso: [platform.country_iso],
        regions: [...(platform.regions || [])],
        origin_countries: [...(platform.origin_countries || [])]
      })
    }

    return acc
  }, [])

  console.log({ groupedPlatforms: groupedPlatforms.length })

  return {
    props: {
      topPlatforms, // Ahora aseguramos que esto es un array
      availablePlatforms: groupedPlatforms,
      country,
      messages: (await import(`../../locales/${locale.split('-')[0]}/common.json`))
        .default
    },
    revalidate: 60 * 60 * 24 * 29
  }
}
